/**
 * Recognising a refused subscription limit.
 *
 * The backend raises these through one channel, `SUBSCRIPTION_LIMIT_EXCEEDED`:
 *
 *   - `subscriptionMiddleware` answers a GraphQL mutation with HTTP 200 and an
 *     `errors[]` entry carrying `extensions.code`, which Apollo surfaces on
 *     `error.graphQLErrors`.
 *   - REST endpoints (the datapoints route) answer 403 with `{ code, error }`.
 *
 * Match on the code, never on the message text — the wording carries the plan
 * name and the current count, and is meant to change.
 */

export const SUBSCRIPTION_LIMIT_CODE = "SUBSCRIPTION_LIMIT_EXCEEDED";

// Some throw sites hand back the internal `CODE:resource:message` form rather
// than the stripped message. Cheap to undo here, and it keeps a leaked prefix
// from ever reaching a user.
const stripPrefix = (message) => {
    if (typeof message !== "string") return message;
    return message.startsWith(`${SUBSCRIPTION_LIMIT_CODE}:`)
        ? message.split(":").slice(2).join(":")
        : message;
};

/**
 * The message to show when an operation was refused for a subscription limit,
 * or null when the error is something else and should be handled normally.
 */
export function subscriptionLimitMessage(error) {
    if (!error) return null;

    const graphQLErrors = error.graphQLErrors ?? [];
    for (const graphQLError of graphQLErrors) {
        if (graphQLError?.extensions?.code === SUBSCRIPTION_LIMIT_CODE) {
            return stripPrefix(graphQLError.message);
        }
    }

    if (error.extensions?.code === SUBSCRIPTION_LIMIT_CODE) {
        return stripPrefix(error.message);
    }

    if (error.code === SUBSCRIPTION_LIMIT_CODE) {
        return stripPrefix(error.error ?? error.message);
    }

    return null;
}

/**
 * Show the subscription message if that is what went wrong, and say whether it
 * was handled. Callers fall through to their usual error handling on false.
 *
 *   if (!handleSubscriptionLimit(err, starekContext)) starekContext.showError(err);
 */
export function handleSubscriptionLimit(error, { showError, loadSubscription, lastUsedWorkspace } = {}) {
    const message = subscriptionLimitMessage(error);
    if (!message) return false;

    showError?.(message);
    // The refusal is itself news about usage — refresh so the limit the UI shows
    // matches the one the server just enforced.
    loadSubscription?.(lastUsedWorkspace);
    return true;
}
